import Vue from 'vue'

const state = {
  scope: null,
  movementOrderItem: null,
  movementRefund: null,
  orderItemsMedia: {},
  creatingRefund: false,
}

export const getters = {
  movementOrderItem: state => state.movementOrderItem,
  movementRefund: state => state.movementRefund,

  movementSiblingItems: state => state.movementOrderItem?.sibling_items || [],
  movementStakeholderSummary: state => state.movementOrderItem?.stakeholder_summary || [],

  getMovementOrderItemMedia: state => orderItemId => {
    return state.orderItemsMedia[orderItemId] || { media: [], loading: false, pagination: null }
  },

  creatingRefund: state => state.creatingRefund,
}

export const mutations = {
  setScope(state, scope) {
    state.scope = scope
  },

  setMovementOrderItem(state, item) {
    state.movementOrderItem = item
  },

  clearMovementOrderItem(state) {
    state.movementOrderItem = null
    state.orderItemsMedia = {}
  },

  setMovementRefund(state, refund) {
    state.movementRefund = refund
  },

  clearMovementRefund(state) {
    state.movementRefund = null
  },

  setCreatingRefund(state, creating) {
    state.creatingRefund = creating
  },

  setOrderItemMediaLoading(state, { orderItemId, loading }) {
    if (!state.orderItemsMedia[orderItemId]) {
      Vue.set(state.orderItemsMedia, orderItemId, { media: [], loading: false, pagination: null })
    }
    Vue.set(state.orderItemsMedia[orderItemId], 'loading', loading)
  },

  setOrderItemMedia(state, { orderItemId, media, pagination }) {
    Vue.set(state.orderItemsMedia, orderItemId, {
      media,
      loading: false,
      pagination: pagination || null
    })
  },

  appendOrderItemMedia(state, { orderItemId, media, pagination }) {
    if (!state.orderItemsMedia[orderItemId]) {
      Vue.set(state.orderItemsMedia, orderItemId, { media: [], loading: false, pagination: null })
    }
    const existing = state.orderItemsMedia[orderItemId].media || []
    Vue.set(state.orderItemsMedia[orderItemId], 'media', [...existing, ...media])
    Vue.set(state.orderItemsMedia[orderItemId], 'pagination', pagination)
    Vue.set(state.orderItemsMedia[orderItemId], 'loading', false)
  },

}

export const actions = accountingService => ({
  async fetchMovementOrderItem({ commit, state }, id) {
    if (!state.scope) {
      throw new Error('Scope not set')
    }

    commit('clearMovementOrderItem')
    const item = await accountingService.fetchMovementOrderItem(state.scope, id)
    commit('setMovementOrderItem', item)
  },

  async fetchMovementRefund({ commit, state }, id) {
    if (!state.scope) {
      throw new Error('Scope not set')
    }

    commit('clearMovementRefund')
    const refund = await accountingService.fetchMovementRefund(state.scope, id)
    commit('setMovementRefund', refund)
  },

  async fetchOrderItemMedia({ commit, state, dispatch }, { orderItemId }) {
    if (!state.scope) {
      throw new Error('Scope not set')
    }

    commit('setOrderItemMediaLoading', { orderItemId, loading: true })
    try {
      const response = await accountingService.fetchMovementOrderItemMedia(state.scope, orderItemId)
      commit('setOrderItemMedia', {
        orderItemId,
        media: response,
        pagination: null
      })
    } catch (e) {
      commit('setOrderItemMedia', { orderItemId, media: [], pagination: null })
      const errorMsg = e?.response?.data?.detail || e?.message || 'Failed to load photos'
      dispatch('notifications/error', errorMsg, { root: true })
    }
  },

  async fetchMoreOrderItemMedia({ state, commit, dispatch }, { orderItemId }) {
    if (!state.scope) {
      throw new Error('Scope not set')
    }

    const current = state.orderItemsMedia[orderItemId]
    if (!current?.pagination?.has_next) return

    commit('setOrderItemMediaLoading', { orderItemId, loading: true })
    try {
      const response = await accountingService.fetchMovementOrderItemMedia(
        state.scope,
        orderItemId,
        current.pagination?.next_cursor
      )
      commit('appendOrderItemMedia', {
        orderItemId,
        media: response,
        pagination: null
      })
    } catch (e) {
      commit('setOrderItemMediaLoading', { orderItemId, loading: false })
      const errorMsg = e?.response?.data?.detail || e?.message || 'Failed to load more photos'
      dispatch('notifications/error', errorMsg, { root: true })
    }
  },

  async createRefund({ commit, state, dispatch }, data) {
    if (!state.scope) {
      throw new Error('Scope not set')
    }

    commit('setCreatingRefund', true)
    try {
      const { order_item_id, ...refundData } = data
      await accountingService.createRefund(state.scope, order_item_id, refundData)
      dispatch('notifications/success', 'Refund created successfully', { root: true })
      // Refresh movement order item data + media
      if (state.movementOrderItem) {
        await dispatch('fetchMovementOrderItem', state.movementOrderItem.id)
        await dispatch('fetchOrderItemMedia', { orderItemId: state.movementOrderItem.id })
      }
      return true
    } catch (e) {
      const errorMsg = e?.response?.data?.detail || e?.message || 'Failed to create refund'
      dispatch('notifications/error', errorMsg, { root: true })
      return false
    } finally {
      commit('setCreatingRefund', false)
    }
  },

})

export default accountingService => ({
  namespaced: true,
  state,
  getters,
  mutations,
  actions: actions(accountingService)
})
